// UGC Ads page (Phase 4).
//
// Filterable list of UGC-eligible Media rows. Each row is expandable
// to show attachments (product / category / branding / promotional —
// operator entries solid, detect entries muted per Phase 1's source
// tagging) and the ads generated against those attachments, grouped
// by campaignKind.
//
// Filter chips (client-side): All / Assigned to product / Assigned
// to category / Branding-only / Promotional / No assignment yet.
// The chip counts render from the per-row assignmentSummary the API
// bundles into GET /api/media?ugc=true (Phase 4.1 backend addition).
//
// Deep link: /ugc-ads?mediaId=<id> auto-expands that row and scrolls
// it into view. Matches the Product Ads UGC-badge click-through
// contract.
//
// W1 pagination: ads-per-target are paginated at 20 with a "view all"
// disclosure so a heavy row doesn't ship a blob of Ad rows into the
// initial render.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box, VStack, HStack, Text, Badge, Button, Image, Input, Spinner,
  Grid, Divider, useToast
} from '@chakra-ui/react';
import { apiJson } from '../../auth/apiFetch';
import { useBrand } from '../../brand/BrandContext';
import { PageHeader } from '../../shell/PageHeader';
// Reuse the Product Ads thumbnail + detail modal so operators get the
// same chrome preview + inspector on both surfaces. AdThumbnail expects
// the ExpansionAd shape (from /api/catalog/:id/ads-detail); /api/ads
// returns a slightly different projectAd shape — the shared toExpansionAd
// adapter reconciles the two.
import { AdThumbnail, AdDetailModal, resolveAndOpenMaster, type ExpansionAd, type RegenerationHistoryEntry } from '../ProductAds';
import { toExpansionAd } from '../../lib/expansionAd';

type MediaRow = {
  mediaId:       string;
  source:        string | null;
  externalId:    string | null;
  fileType:      string | null;
  fileUrl:       string | null;
  brand:         string | null;
  caption:       string | null;
  postedAt:      string | null;
  creatorHandle: string | null;
  postType:      string | null;
  socialPostType: string | null;
  assignmentSummary: {
    operatorProducts:   number;
    detectProducts:     number;
    operatorCategories: number;
    detectCategories:   number;
    branding:           boolean;
    promotional:        boolean;
  };
  // Phase 6 additions from the backend row hint.
  hasLegacyUgc?:  boolean;
  deletedAt?:     string | null;
};

type AssignmentsResp = {
  ok: true;
  media: {
    _id: string;
    fileUrl: string | null;
    fileType: string | null;
    source: string | null;
    externalId: string | null;
    socialPostType: string | null;
  };
  products: Array<{
    catalogProductId: string | null;
    title: string | null;
    imageUrl: string | null;
    source: 'detect' | 'operator';
  }>;
  categories: Array<{
    categoryId: string | null;
    name: string | null;
    breadcrumbKey: string | null;
    source: 'detect' | 'operator';
  }>;
  branding: { assignedAt: string } | null;
  promotional: { assignedAt: string; productIds: string[] } | null;
};

// Raw /api/ads projectAd shape — union of everything the endpoint
// returns that we care about. Adapted into ExpansionAd via
// toExpansionAd() before rendering with AdThumbnail.
type Ad = {
  id:             string;
  status:         string;
  variantKind:    string | null;
  template:       string | null;
  campaignId:     string | null;
  campaignKind:   string | null;
  aspectRatio:    string | null;
  platformFormat: string | null;
  productId:      string | null;
  productTitle?:  string | null;
  // Intent profile + retailer product-page link — see the matching
  // fields on ExpansionAd (pages/ProductAds/index.tsx) for the full
  // null-safety contract. Present on /api/ads' projectAd() response
  // since PR #263; declared here so toExpansionAd() can forward them.
  funnelStage?:   'awareness' | 'consideration' | 'conversion' | null;
  // THE canonical "is this ad a true video master" answer — see the
  // matching field on ExpansionAd (pages/ProductAds/index.tsx) for the
  // full contract. /api/ads' projectAd() emits this unconditionally.
  isMaster?:      boolean | null;
  productUrl?:    string | null;
  renderUrl:      string | null;
  thumbnailUrl:   string | null;
  posterUrl?:     string | null;
  photorealUrl?:  string | null;
  previewVideoUrl?: string | null;
  previewImageUrl?: string | null;
  useImageRefAsProduction?: boolean;
  kind?:          'image' | 'video';
  sourceFileType?: 'image' | 'video' | null;
  copy?:          { headline?: string | null; cta_text?: string | null };
  ctaText?:       string | null;
  approved?:      boolean;
  approvedAt?:    string | null;
  renderedAt?:    string | null;
  metaSyncStatus?: string | null;
  metaAdId?:      string | null;
  metaAdsetId?:   string | null;
  regenerating?:  boolean;
  regenerationStage?: string | null;
  regenerationHistory?: RegenerationHistoryEntry[];
  mediaId?:       string | null;
  createdAt:      string;
  renderStage?:        string | null;
  renderStageAt?:      string | null;
  renderErrorMessage?: string | null;
  visionQc?: ExpansionAd['visionQc'];
  // Canonical phase + failure classification from projectAd(). Forwarded
  // by the shared toExpansionAd so Override QC is reachable on this page.
  phase?:   string | null;
  failure?: ExpansionAd['failure'];
  qcOverridden?:     boolean;
  qcOverriddenAt?:   string | null;
  qcOverriddenBy?:   string | null;
  qcOverrideReason?: string | null;
  // Master↔derivative link — forward-compatible passthrough, same as
  // previewVideoUrl/previewImageUrl above. Absent on every /api/ads response
  // today (backend follow-up not shipped); declared here so this adapter
  // doesn't need a second edit the day it is.
  masterAdId?:           string | null;
  masterThumbnailUrl?:   string | null;
  masterStatus?:         string | null;
  masterPlatformFormat?: string | null;
  derivativeCount?:      number | null;
};

type FilterKey = 'all' | 'product' | 'category' | 'branding' | 'promotional' | 'none' | 'legacy';

// Kind → display copy + chip color used inside the expand.
const KIND_META: Record<string, { label: string; color: string }> = {
  product:     { label: 'Product',    color: 'purple' },
  collection:  { label: 'Category',   color: 'blue'   },
  brand:       { label: 'Brand',      color: 'green'  },
  promotional: { label: 'Promo',      color: 'orange' }
};

export function UgcAdsPage() {
  const { activeBrand } = useBrand();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const deepLinkMediaId = params.get('mediaId');
  const [rows, setRows]         = useState<MediaRow[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [filter, setFilter]     = useState<FilterKey>('all');
  const [q, setQ]               = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(() =>
    deepLinkMediaId ? new Set([deepLinkMediaId]) : new Set()
  );
  const rowRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());
  // Product-Ads-parity ad detail modal. Same state pattern (single
  // "which ad is open") lifted to the page level so any row's ad can
  // open it — the modal is a Chakra Modal so React handles the stacking.
  const [openAd, setOpenAd] = useState<ExpansionAd | null>(null);
  const openAdIdRef = useRef<string | null>(null);
  openAdIdRef.current = openAd?.adId ?? null;

  useEffect(() => {
    if (!activeBrand?.id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const res = await apiJson<{ media: MediaRow[] }>(
          `/api/media?brandId=${encodeURIComponent(activeBrand.id)}&ugc=true&limit=100`
        );
        if (cancelled) return;
        setRows(res.media || []);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load UGC');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeBrand?.id]);

  // Scroll deep-linked row into view once its row element exists.
  // useEffect runs after render, so the ref is available on the next
  // tick — small setTimeout keeps this resilient to the row being in a
  // suspense boundary if we ever add one.
  useEffect(() => {
    if (!deepLinkMediaId) return;
    const el = rowRefs.current.get(deepLinkMediaId);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
    }
  }, [deepLinkMediaId, rows]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter(r => {
      // Filter chip
      const s = r.assignmentSummary;
      if (filter === 'product'     && s.operatorProducts   === 0) return false;
      if (filter === 'category'    && s.operatorCategories === 0) return false;
      if (filter === 'branding'    && !s.branding)                return false;
      if (filter === 'promotional' && !s.promotional)             return false;
      if (filter === 'none' && (
        s.operatorProducts + s.operatorCategories > 0 || s.branding || s.promotional
      )) return false;
      // Phase 6 — Legacy chip: only rows that source at least one
      // variantKind='ugc' ad AND have no operator attachment on the
      // Media itself (the true "auto-attached" set). Backend rowHint
      // hasLegacyUgc is a superset (any legacy ad exists on the media)
      // so we intersect with the no-operator-attachment check here.
      if (filter === 'legacy') {
        const noOperator = s.operatorProducts + s.operatorCategories === 0 && !s.branding && !s.promotional;
        if (!r.hasLegacyUgc || !noOperator) return false;
      }
      // Text filter
      if (term) {
        return (
          (r.caption || '').toLowerCase().includes(term) ||
          (r.creatorHandle || '').toLowerCase().includes(term) ||
          (r.externalId || '').toLowerCase().includes(term)
        );
      }
      return true;
    });
  }, [rows, filter, q]);

  // Chip counts derived off the FULL unfiltered set so switching chip
  // doesn't zero out other chips.
  const counts = useMemo(() => {
    const c = { all: rows.length, product: 0, category: 0, branding: 0, promotional: 0, none: 0, legacy: 0 };
    for (const r of rows) {
      const s = r.assignmentSummary;
      const noOperator = s.operatorProducts + s.operatorCategories === 0 && !s.branding && !s.promotional;
      if (s.operatorProducts   > 0) c.product     += 1;
      if (s.operatorCategories > 0) c.category    += 1;
      if (s.branding)               c.branding    += 1;
      if (s.promotional)            c.promotional += 1;
      if (noOperator)               c.none        += 1;
      if (r.hasLegacyUgc && noOperator) c.legacy   += 1;
    }
    return c;
  }, [rows]);

  const toggleExpand = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    // Sync ?mediaId= so a refresh keeps the row open. Only when
    // uniquely expanding — collapsing clears it.
    if (deepLinkMediaId === id) {
      const np = new URLSearchParams(params);
      np.delete('mediaId');
      setParams(np, { replace: true });
    }
  };

  return (
    <VStack align="stretch" spacing={6}>
      <PageHeader
        eyebrow="UGC Ads"
        title="UGC posts and attached ads"
        description="Every UGC post the brand has ingested or an operator has attached to a product, category, brand identity, or promotional target. Expand a row to see attachments and the ads generated from them."
        primaryAction={
          <Button as={RouterLink} to="/ugc-wizard" variant="brand" size="sm">
            + New from UGC
          </Button>
        }
      />

      <HStack spacing={2} flexWrap="wrap">
        <FilterChip label="All"           n={counts.all}         active={filter === 'all'}         onClick={() => setFilter('all')} />
        <FilterChip label="Product"       n={counts.product}     active={filter === 'product'}     onClick={() => setFilter('product')} />
        <FilterChip label="Category"      n={counts.category}    active={filter === 'category'}    onClick={() => setFilter('category')} />
        <FilterChip label="Branding-only" n={counts.branding}    active={filter === 'branding'}    onClick={() => setFilter('branding')} />
        <FilterChip label="Promotional"   n={counts.promotional} active={filter === 'promotional'} onClick={() => setFilter('promotional')} />
        <FilterChip label="Unassigned"    n={counts.none}        active={filter === 'none'}        onClick={() => setFilter('none')} />
        <FilterChip label="Legacy"        n={counts.legacy}      active={filter === 'legacy'}      onClick={() => setFilter('legacy')} />
      </HStack>

      <Input
        size="sm"
        placeholder="Filter by caption, handle, or post id"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        maxW="520px"
        bg="white"
      />

      {loading && <HStack py={6}><Spinner size="sm" /><Text fontSize="sm" color="brand.muted">Loading UGC…</Text></HStack>}
      {error && <Text fontSize="sm" color="red.600">{error}</Text>}

      {!loading && filtered.length === 0 && (
        <Box borderWidth="1px" borderStyle="dashed" borderColor="brand.border" borderRadius="lg" p={6}>
          <Text fontSize="sm" color="brand.muted">
            No UGC matches the current filter. Connect Instagram or run an Apify scrape from Integrations to ingest social media, or use the wizard to attach a manual upload.
          </Text>
        </Box>
      )}

      <VStack align="stretch" spacing={3}>
        {filtered.map(r => (
          <MediaRowCard
            key={r.mediaId}
            row={r}
            isExpanded={expanded.has(r.mediaId)}
            onToggle={() => toggleExpand(r.mediaId)}
            registerRef={(el) => { rowRefs.current.set(r.mediaId, el); }}
            highlighted={deepLinkMediaId === r.mediaId}
            onOpenAd={setOpenAd}
          />
        ))}
      </VStack>

      {openAd && activeBrand?.id && (
        <AdDetailModal
          ad={openAd}
          brandId={activeBrand.id}
          onClose={() => setOpenAd(null)}
          onOpenMaster={(masterAdId) => {
            if (!activeBrand?.id) return;
            // This page's ad lists are scoped per UGC media item, not per
            // product — a video master is unlikely to be among them, so go
            // straight to the single-ad fetch fallback rather than pretend
            // an in-memory lookup is worth trying here.
            const fromAdId = openAd?.adId;
            void resolveAndOpenMaster({
              masterAdId,
              brandId: activeBrand.id,
              siblingAds: null,
              isCurrent: () => openAdIdRef.current === fromAdId,
              setOpenAd,
              toast
            });
          }}
          onMutated={(updated) => {
            // Late arrival guard — a copy-save or regen kicked off before
            // close / switch can resolve after; don't overwrite a since-
            // opened different ad. Same pattern as Product Ads uses.
            if (openAdIdRef.current !== updated.adId) return;
            setOpenAd(updated);
          }}
        />
      )}
    </VStack>
  );
}

function FilterChip({ label, n, active, onClick }: { label: string; n: number; active: boolean; onClick: () => void }) {
  return (
    <Box
      as="button"
      type="button"
      onClick={onClick}
      px={3}
      py={1.5}
      borderWidth="1px"
      borderColor={active ? 'rsViolet.500' : 'brand.border'}
      bg={active ? 'rsViolet.500' : 'white'}
      color={active ? 'white' : 'brand.ink'}
      borderRadius="full"
      fontSize="12px"
      fontWeight="700"
      cursor="pointer"
      transition="border-color 120ms, background 120ms"
    >
      {label}
      <Badge
        ml={2}
        fontSize="9px"
        colorScheme={active ? 'whiteAlpha' : 'gray'}
        variant="subtle"
      >
        {n}
      </Badge>
    </Box>
  );
}

function MediaRowCard({
  row, isExpanded, onToggle, registerRef, highlighted, onOpenAd
}: {
  row:         MediaRow;
  isExpanded:  boolean;
  onToggle:    () => void;
  registerRef: (el: HTMLDivElement | null) => void;
  highlighted: boolean;
  onOpenAd:    (ad: ExpansionAd) => void;
}) {
  const s = row.assignmentSummary;
  return (
    <Box
      ref={registerRef}
      borderWidth={highlighted ? '2px' : '1px'}
      borderColor={highlighted ? 'rsViolet.400' : 'brand.border'}
      borderRadius="lg"
      bg="brand.surface"
      overflow="hidden"
    >
      <HStack align="stretch" spacing={0}>
        <Box w="120px" flexShrink={0} bg="brand.canvas">
          {row.fileType === 'video' && row.fileUrl ? (
            <video
              src={row.fileUrl}
              muted
              loop
              playsInline
              style={{ display: 'block', width: '100%', height: '120px', objectFit: 'cover' }}
            />
          ) : row.fileUrl ? (
            <Image
              src={row.fileUrl}
              alt={row.caption || 'UGC'}
              w="100%"
              h="120px"
              objectFit="cover"
              display="block"
            />
          ) : (
            <Box w="100%" h="120px" />
          )}
        </Box>

        <Box flex={1} minW={0} px={4} py={3}>
          <HStack spacing={2} mb={1} flexWrap="wrap">
            {row.source && <Badge fontSize="9px" colorScheme="purple" variant="subtle">{row.source}</Badge>}
            {row.postType && <Badge fontSize="9px" colorScheme="gray" variant="subtle">{row.postType}</Badge>}
            {row.socialPostType && <Badge fontSize="9px" colorScheme="gray" variant="subtle">{row.socialPostType}</Badge>}
            {row.hasLegacyUgc && (
              <Badge fontSize="9px" colorScheme="yellow" variant="subtle" title="Sourced at least one variantKind='ugc' ad">
                Legacy UGC
              </Badge>
            )}
            {row.deletedAt && (
              <Badge fontSize="9px" colorScheme="red" variant="subtle" title="Source Media soft-deleted">
                Deleted
              </Badge>
            )}
            {row.creatorHandle && (
              <Text fontSize="xs" fontWeight="700" color="brand.ink">@{row.creatorHandle}</Text>
            )}
          </HStack>
          {row.caption && (
            <Text fontSize="xs" color="brand.muted" noOfLines={2} mb={2}>{row.caption}</Text>
          )}

          <HStack spacing={2} flexWrap="wrap">
            {s.operatorProducts > 0 && (
              <Badge colorScheme="purple" variant="solid" fontSize="9px">
                {s.operatorProducts} product{s.operatorProducts === 1 ? '' : 's'}
              </Badge>
            )}
            {s.operatorCategories > 0 && (
              <Badge colorScheme="blue" variant="solid" fontSize="9px">
                {s.operatorCategories} categor{s.operatorCategories === 1 ? 'y' : 'ies'}
              </Badge>
            )}
            {s.branding && (
              <Badge colorScheme="green" variant="solid" fontSize="9px">Branding</Badge>
            )}
            {s.promotional && (
              <Badge colorScheme="orange" variant="solid" fontSize="9px">Promotional</Badge>
            )}
            {(s.detectProducts > 0 || s.detectCategories > 0) && (
              // Detect-derived attachments render muted per the spec —
              // they exist but they're not "the operator picked this".
              <Badge colorScheme="gray" variant="subtle" fontSize="9px">
                {s.detectProducts + s.detectCategories} detected
              </Badge>
            )}
            {s.operatorProducts + s.operatorCategories === 0 && !s.branding && !s.promotional && (
              <Badge colorScheme="gray" variant="outline" fontSize="9px">No attachments</Badge>
            )}
          </HStack>
        </Box>

        <VStack align="stretch" spacing={2} px={3} py={3} borderLeftWidth="1px" borderLeftColor="brand.border" justify="center">
          <Button
            as={RouterLink}
            to={`/ugc-wizard?ugc=${row.mediaId}`}
            size="xs"
            variant="brand"
          >
            Attach + generate
          </Button>
          <Button size="xs" variant="outline" onClick={onToggle}>
            {isExpanded ? 'Collapse' : 'Expand'}
          </Button>
        </VStack>
      </HStack>

      {isExpanded && (
        <>
          <Divider />
          <ExpandedRow mediaId={row.mediaId} onOpenAd={onOpenAd} />
        </>
      )}
    </Box>
  );
}

// Lazily loads assignments + ads for one media row on expand. Keeps
// the initial page render cheap even when hundreds of UGC rows exist.
// Ads-per-target paginate at ADS_INITIAL with a "View all N" disclosure
// per the spec's W1 pagination watchout.
const ADS_INITIAL = 20;

function ExpandedRow({ mediaId, onOpenAd }: { mediaId: string; onOpenAd: (ad: ExpansionAd) => void }) {
  const { activeBrand } = useBrand();
  const [assignments, setAssignments] = useState<AssignmentsResp | null>(null);
  const [ads, setAds]                 = useState<Ad[]>([]);
  // Product title lookup — hydrated in the same load. Null → row still
  // renders with "(product removed)" per Phase 6 W1.
  const [productTitles, setProductTitles] = useState<Map<string, string>>(new Map());
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [showAllAds, setShowAllAds]   = useState(false);
  const [reloadBump, setReloadBump]   = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        // Fire both loads in parallel — assignments hydrates chips + the
        // attachments list, ads populates the campaignKind-grouped strip.
        const [a, adResp] = await Promise.all([
          apiJson<AssignmentsResp>(`/api/media/${mediaId}/assignments`),
          activeBrand?.id
            // No `rendered=true` (deliberately, added by the ad-review
            // consistency pass) — that param's backend filter silently
            // excludes 'failed' too, not just the in-progress queue it was
            // meant to hide (routes/ads.js's own comment: "surfaces that
            // shouldn't surface the queue"). Fetch everything and drop only
            // the genuine queue client-side below, so 'failed' rows survive
            // and the failure-detail UI in AdDetailModal has something to show.
            ? apiJson<{ ads: Ad[] }>(
                `/api/ads?brandId=${encodeURIComponent(activeBrand.id)}&mediaId=${mediaId}&limit=200`
              )
            : Promise.resolve({ ads: [] as Ad[] })
        ]);
        if (cancelled) return;
        setAssignments(a);
        setAds((adResp.ads || []).filter(x => x.status !== 'queued' && x.status !== 'rendering'));

        // Hydrate product titles for legacy-ad rows. GET /api/catalog
        // supports ?ids= batch lookup; a missing id in the response is
        // a hard-deleted product (Phase 6 W1) — we render "(product
        // removed)" for those below.
        const distinctPids = Array.from(new Set(
          (adResp.ads || [])
            .map(ad => ad.productId)
            .filter((pid): pid is string => !!pid)
        ));
        if (activeBrand?.id && distinctPids.length) {
          try {
            // GET /api/catalog?ids= returns rows shaped by projectListRow —
            // `id: String(p._id)`, not productId. Not the same shape as
            // /api/catalog/ads-summary. Use `id` here.
            const catResp = await apiJson<{ products: Array<{ id: string; title: string }> }>(
              `/api/catalog?brandId=${encodeURIComponent(activeBrand.id)}&ids=${distinctPids.join(',')}&limit=${distinctPids.length}`
            );
            if (!cancelled) {
              const m = new Map<string, string>();
              for (const p of catResp.products || []) m.set(String(p.id), p.title || '');
              setProductTitles(m);
            }
          } catch { /* non-fatal — rows fall back to "(product removed)" */ }
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mediaId, activeBrand?.id, reloadBump]);

  // Split ads into legacy vs operator-associated. An ad is LEGACY when:
  //   • variantKind === 'ugc' (predates the Phase 1 pipeline), AND
  //   • its productId is NOT in the media's operator-attached-products
  //     set (i.e. no Phase 1 assignment ties this UGC to that SKU).
  // That double gate matters: a Media that has SOME operator products
  // AND SOME legacy auto-attached ads on OTHER products should still
  // surface the operator ones in their normal kind bucket. Only the
  // truly auto-attached rows go to the Legacy bucket.
  const operatorProductIdSet = useMemo(() => {
    if (!assignments) return new Set<string>();
    return new Set(
      assignments.products
        .filter(p => p.source === 'operator' && p.catalogProductId)
        .map(p => String(p.catalogProductId))
    );
  }, [assignments]);

  const { adsByKind, legacyAds } = useMemo(() => {
    const byKind = new Map<string, Ad[]>();
    const legacy: Ad[] = [];
    for (const ad of ads) {
      const isLegacy = ad.variantKind === 'ugc'
        && (!ad.productId || !operatorProductIdSet.has(String(ad.productId)));
      if (isLegacy) {
        legacy.push(ad);
      } else {
        const k = ad.campaignKind || 'unknown';
        const list = byKind.get(k) || [];
        list.push(ad);
        byKind.set(k, list);
      }
    }
    return { adsByKind: byKind, legacyAds: legacy };
  }, [ads, operatorProductIdSet]);

  if (loading) return <Box p={4}><Spinner size="sm" /></Box>;
  if (error) return <Box p={4}><Text fontSize="sm" color="red.600">{error}</Text></Box>;
  if (!assignments) return null;

  const operatorProducts   = assignments.products.filter(p => p.source === 'operator');
  const detectProducts     = assignments.products.filter(p => p.source === 'detect');
  const operatorCategories = assignments.categories.filter(c => c.source === 'operator');
  const detectCategories   = assignments.categories.filter(c => c.source === 'detect');

  return (
    <Box p={4}>
      <Grid templateColumns={{ base: '1fr', md: '1fr 2fr' }} gap={6}>
        <VStack align="stretch" spacing={3}>
          <SectionLabel>Attachments</SectionLabel>
          {operatorProducts.length + detectProducts.length > 0 && (
            <Box>
              <MutedLabel>Products</MutedLabel>
              <VStack align="stretch" spacing={1}>
                {operatorProducts.map(p => <AttachmentRow key={p.catalogProductId} title={p.title} imageUrl={p.imageUrl} operator />)}
                {detectProducts.map(p =>   <AttachmentRow key={`d-${p.catalogProductId}`} title={p.title} imageUrl={p.imageUrl} operator={false} />)}
              </VStack>
            </Box>
          )}
          {operatorCategories.length + detectCategories.length > 0 && (
            <Box>
              <MutedLabel>Categories</MutedLabel>
              <VStack align="stretch" spacing={1}>
                {operatorCategories.map(c => <AttachmentRow key={c.categoryId} title={c.name} operator />)}
                {detectCategories.map(c =>   <AttachmentRow key={`d-${c.categoryId}`} title={c.name} operator={false} />)}
              </VStack>
            </Box>
          )}
          {assignments.branding && (
            <Box>
              <MutedLabel>Branding</MutedLabel>
              <AttachmentRow title="Attached as brand identity" operator />
            </Box>
          )}
          {assignments.promotional && (
            <Box>
              <MutedLabel>Promotional</MutedLabel>
              <AttachmentRow
                title={`Promotional${assignments.promotional.productIds.length ? ` (${assignments.promotional.productIds.length} callouts)` : ''}`}
                operator
              />
            </Box>
          )}
          {operatorProducts.length + detectProducts.length + operatorCategories.length + detectCategories.length === 0
            && !assignments.branding && !assignments.promotional && (
            <Text fontSize="xs" color="brand.muted">No attachments yet.</Text>
          )}
        </VStack>

        <VStack align="stretch" spacing={3}>
          <SectionLabel>Ads generated ({ads.length})</SectionLabel>
          {ads.length === 0 && (
            <Text fontSize="xs" color="brand.muted">
              No ads have been generated from this UGC yet. Use the wizard to attach it to a target and generate.
            </Text>
          )}
          {Array.from(adsByKind.entries()).map(([kind, list]) => (
            <KindGroup key={kind} kind={kind} list={list} showAllAds={showAllAds} onOpenAd={onOpenAd} />
          ))}
          {legacyAds.length > 0 && (
            <LegacyBucket
              ads={legacyAds}
              productTitles={productTitles}
              mediaId={mediaId}
              showAll={showAllAds}
              onConverted={() => setReloadBump(b => b + 1)}
              onOpenAd={onOpenAd}
            />
          )}
          {ads.length > ADS_INITIAL && !showAllAds && (
            <Button size="xs" variant="link" onClick={() => setShowAllAds(true)} alignSelf="flex-start">
              View all {ads.length} ads
            </Button>
          )}
        </VStack>
      </Grid>
    </Box>
  );
}

// Phase 6 — "Legacy (auto-attached)" bucket. Ads generated before the
// Phase 1 assign pipeline existed, so they have mediaId but no operator
// assignment. Read-only visibility of the complete history. Optional
// "Convert" button attaches the ad's productId as an operator assignment
// so future generation flows use the same UGC seed (Phase 3 seed cascade
// picks it up via preferUgcMediaId in the wizard).
function LegacyBucket({
  ads, productTitles, mediaId, showAll, onConverted, onOpenAd
}: {
  ads:           Ad[];
  productTitles: Map<string, string>;
  mediaId:       string;
  showAll:       boolean;
  onConverted:   () => void;
  onOpenAd:      (ad: ExpansionAd) => void;
}) {
  const toast = useToast();
  const [converting, setConverting] = useState<Set<string>>(new Set());
  const visible = showAll ? ads : ads.slice(0, ADS_INITIAL);

  const handleConvert = async (productId: string) => {
    setConverting(prev => new Set(prev).add(productId));
    try {
      await apiJson(`/api/media/${mediaId}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetType: 'product', targetId: productId })
      });
      toast({
        title: 'Converted to operator attachment',
        description: 'This UGC will seed future ads for the product.',
        status: 'success',
        duration: 3000
      });
      onConverted();
    } catch (err) {
      toast({
        title: 'Convert failed',
        description: err instanceof Error ? err.message : 'unknown error',
        status: 'error'
      });
    } finally {
      setConverting(prev => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  };

  return (
    <Box borderWidth="1px" borderStyle="dashed" borderColor="brand.border" borderRadius="md" p={3} bg="brand.canvas">
      <HStack spacing={2} mb={2}>
        <Badge colorScheme="yellow" variant="solid" fontSize="9px">Legacy (auto-attached)</Badge>
        <Text fontSize="10px" color="brand.muted">
          {ads.length} ad{ads.length === 1 ? '' : 's'} · read-only, from before the operator-attach pipeline
        </Text>
      </HStack>
      {/* Same tile geometry as the operator-associated grid — AdThumbnail
          on top, product title + Convert button below. Click the
          thumbnail to open AdDetailModal like Product Ads; Convert stays
          a separate control so an accidental tile click doesn't attach. */}
      <Grid templateColumns="repeat(auto-fill, minmax(160px, 1fr))" gap={2}>
        {visible.map(ad => {
          const pid   = ad.productId ? String(ad.productId) : null;
          // W1 — a null productId or a productId that didn't come back
          // from the catalog batch lookup means the product was hard-
          // deleted. Render "(product removed)" instead of blanking.
          const hydratedTitle = pid ? productTitles.get(pid) : null;
          const missing = !pid || !hydratedTitle;
          const label   = missing ? '(product removed)' : hydratedTitle;
          const isBusy  = pid ? converting.has(pid) : false;
          const exp     = toExpansionAd(ad);
          return (
            <Box
              key={ad.id}
              borderWidth="1px"
              borderColor="brand.border"
              borderRadius="md"
              overflow="hidden"
              bg="white"
              display="flex"
              flexDirection="column"
            >
              <Box onClick={() => onOpenAd(exp)} cursor="pointer">
                <AdThumbnail ad={exp} />
              </Box>
              <Box px={2} py={2} flex="1 1 auto" display="flex" flexDirection="column" gap={1}>
                <Text
                  fontSize="10px"
                  fontWeight="700"
                  color={missing ? 'brand.muted' : 'brand.ink'}
                  noOfLines={2}
                  fontStyle={missing ? 'italic' : 'normal'}
                >
                  {label}
                </Text>
                {/* Convert disabled when the product was hard-deleted —
                    no target for mediaAssignmentService.attachProduct. */}
                <Button
                  size="xs"
                  variant="outline"
                  isDisabled={missing || isBusy}
                  isLoading={isBusy}
                  loadingText="Converting"
                  onClick={() => pid && handleConvert(pid)}
                  title={missing ? 'Product removed — cannot attach' : 'Attach this UGC to the ad\'s product as an operator assignment'}
                  mt="auto"
                >
                  Convert
                </Button>
              </Box>
            </Box>
          );
        })}
      </Grid>
    </Box>
  );
}

function KindGroup({
  kind, list, showAllAds, onOpenAd
}: {
  kind:       string;
  list:       Ad[];
  showAllAds: boolean;
  onOpenAd:   (ad: ExpansionAd) => void;
}) {
  const meta = KIND_META[kind] || { label: kind, color: 'gray' };
  const visible = showAllAds ? list : list.slice(0, ADS_INITIAL);
  return (
    <Box>
      <HStack spacing={2} mb={2}>
        <Badge colorScheme={meta.color} variant="solid" fontSize="9px">{meta.label}</Badge>
        <Text fontSize="10px" color="brand.muted">{list.length} ad{list.length === 1 ? '' : 's'}</Text>
      </HStack>
      {/* Grid width matches Product Ads' expansion (auto-fill min 160px)
          so the chrome preview inside AdThumbnail has room to render
          CTA buttons + safe-zone gutters legibly. 96px is too tight for
          the Meta chrome. */}
      <Grid templateColumns="repeat(auto-fill, minmax(160px, 1fr))" gap={2}>
        {visible.map(ad => {
          const exp = toExpansionAd(ad);
          return (
            <Box key={ad.id} onClick={() => onOpenAd(exp)} cursor="pointer">
              <AdThumbnail ad={exp} />
            </Box>
          );
        })}
      </Grid>
    </Box>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Text
      fontSize="11px"
      fontWeight="700"
      textTransform="uppercase"
      letterSpacing="0.06em"
      color="brand.muted"
    >
      {children}
    </Text>
  );
}

function MutedLabel({ children }: { children: React.ReactNode }) {
  return (
    <Text fontSize="10px" fontWeight="700" color="brand.muted" mb={1}>
      {children}
    </Text>
  );
}

function AttachmentRow({ title, imageUrl, operator }: { title: string | null; imageUrl?: string | null; operator: boolean }) {
  return (
    <HStack
      spacing={2}
      p={1.5}
      borderRadius="md"
      borderWidth="1px"
      borderColor="brand.border"
      bg="white"
      opacity={operator ? 1 : 0.6}
    >
      {imageUrl && <Image src={imageUrl} boxSize="24px" objectFit="cover" borderRadius="sm" alt="" />}
      <Text fontSize="xs" fontWeight={operator ? '700' : '500'} color="brand.ink" noOfLines={1} flex={1}>
        {title || '—'}
      </Text>
      {!operator && (
        <Badge fontSize="9px" colorScheme="gray" variant="subtle">detect</Badge>
      )}
    </HStack>
  );
}
